import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Briefcase, Loader2, Filter, Search, AlertTriangle, CheckCircle2, XCircle,
  MapPin, ChevronLeft, ChevronRight,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/dates";

const PAGE_SIZE = 20;

export default function AdminJobs() {
  const { t } = useTranslation();
  const STATUS_OPTS = [
    { v: "", label: t("adminX.jobs.allStatus") },
    { v: "open", label: t("adminX.jobs.open") },
    { v: "in_progress", label: t("adminX.jobs.inProgress") },
    { v: "completed", label: t("adminX.jobs.completed") },
    { v: "canceled", label: t("adminX.jobs.canceled") },
    // T19. Deleted posts are hidden from every other option; this is the
    // one place they can be found.
    { v: "deleted", label: t("adminX.jobs.deleted") },
  ];

  const MOD_OPTS = [
    { v: "", label: t("adminX.jobs.allMod") },
    { v: "approved", label: t("adminX.jobs.approved") },
    { v: "flagged", label: t("adminX.jobs.flagged") },
  ];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const [moderation, setModeration] = useState("");
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
      if (status) params.set("status", status);
      if (moderation) params.set("moderation_status", moderation);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/jobs?${params}`);
      setItems(data.jobs || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, status, moderation, q]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (jobId, action, reason) => {
    if (action === "reject" && !window.confirm(t("adminX.jobs.softDeleteConfirm"))) return;
    try {
      const url = `/admin/jobs/${jobId}/${action}`;
      const params = new URLSearchParams();
      if (reason) params.set("reason", reason);
      await api.patch(`${url}?${params}`);
      load();
    } catch (e) {
      alert(errorMessage(e, t("adminX.jobs.actionFailed")));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-jobs-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("adminX.jobs.title")}</h1>
            <p className="mt-2 text-smoke">{t("adminX.jobs.subtitle")}</p>
          </div>
          <span className="badge">{t("adminX.jobs.total", { n: total })}</span>
        </div>

        <div className="card-base p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="admin-jobs-filters">
          <Filter size={16} className="text-smoke" />
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            className="input-base !py-1.5 !text-sm w-auto" data-testid="status-filter">
            {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <select value={moderation} onChange={(e) => { setModeration(e.target.value); setPage(0); }}
            className="input-base !py-1.5 !text-sm w-auto" data-testid="mod-filter">
            {MOD_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <form onSubmit={(e) => { e.preventDefault(); setQ(searchInput); setPage(0); }}
            className="flex items-center gap-2 flex-1 min-w-[240px]" data-testid="search-form">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                className="input-base pl-10 !py-1.5 !text-sm"
                placeholder={t("adminX.jobs.searchPh")} data-testid="search-input" />
            </div>
            <button type="submit" className="fx-btn fx-btn--secondary text-sm">{t("adminX.jobs.search")}</button>
          </form>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-smoke">
            <Loader2 className="animate-spin mr-2" /> {t("adminX.jobs.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="card-base p-12 text-center text-smoke">
            <Briefcase size={48} className="mx-auto text-hive-ink mb-3" />
            <p className="font-display text-lg font-semibold text-worker">{t("adminX.jobs.noJobs")}</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {items.map((j) => <Row key={j.job_id} t={t} job={j} onModerate={moderate} />)}
            </div>
            <Pager t={t} page={page} totalPages={totalPages} onPrev={() => setPage(Math.max(0, page - 1))}
              onNext={() => setPage(Math.min(totalPages - 1, page + 1))} />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// The badge's word for each status, from the same keys the filter reads.
const STATUS_KEY = {
  open: "open", in_progress: "inProgress", completed: "completed", canceled: "canceled",
};

/**
 * T19. A deleted post and a job canceled in progress both carry
 * `status = canceled`; the badge used to print that raw word for both. A
 * deleted post now reads "Eliminado" in the badge itself — `is_deleted`,
 * from `deleted_at` — and "Cancelado" is left to the jobs that were canceled,
 * which carry their reason underneath.
 */
function statusLabel(t, job) {
  if (job.is_deleted) return t("adminX.jobs.deleted");
  const key = STATUS_KEY[job.status];
  return key ? t(`adminX.jobs.${key}`) : job.status.replace(/_/g, " ");
}

function Row({ t, job, onModerate }) {
  const flagged = job.moderation_status === "flagged";
  return (
    <div className="card-base p-5" data-testid={`admin-job-${job.job_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="badge" data-testid={`admin-job-status-${job.job_id}`}>{statusLabel(t, job)}</span>
            {flagged && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-display inline-flex items-center gap-1">
                <AlertTriangle size={11} /> {t("adminX.jobs.flagged")}
              </span>
            )}
          </div>
          <p className="font-display font-bold text-worker">{job.title}</p>
          <p className="text-sm text-smoke line-clamp-2 mt-1">{job.description}</p>
          {flagged && job.moderation_reason && (
            <p className="text-xs text-red-700 mt-1 italic">{t("adminX.jobs.reasonLabel")} {job.moderation_reason}</p>
          )}
          {/* T19 X1. Why the requester cancelled a job in progress — the
              admin's half of the same fact the FixyBee was told. */}
          {job.canceled_at && job.cancel_reason && (
            <p className="text-xs text-smoke mt-1 italic" data-testid={`admin-job-cancel-reason-${job.job_id}`}>
              {t("adminX.jobs.reasonLabel")} {job.cancel_reason}
            </p>
          )}
          <div className="mt-2 text-xs text-smoke flex items-center gap-3 flex-wrap">
            <span className="font-mono">{job.job_id}</span>
            <span><MapPin size={11} className="inline mr-0.5" />{job.location?.city}, {job.location?.state}</span>
            <span>${job.budget_min?.toFixed(0)}-${job.budget_max?.toFixed(0)}</span>
            <span className="font-mono">{formatDate(job.created_at)}</span>
          </div>
          {job.client_snippet && (
            <p className="text-xs text-smoke mt-2">
              <strong>{t("adminX.jobs.client")}</strong> {job.client_snippet.full_name || "—"} ({job.client_snippet.email})
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 items-end">
          {flagged && !job.is_deleted && (
            <>
              <button onClick={() => onModerate(job.job_id, "approve")}
                className="fx-btn fx-btn--secondary text-xs border-green-300 text-green-700 hover:bg-green-50"
                data-testid={`approve-${job.job_id}`}>
                <CheckCircle2 size={12} /> {t("adminX.jobs.approveBtn")}
              </button>
              <button onClick={() => onModerate(job.job_id, "reject", t("adminX.jobs.reasonInappropriate"))}
                className="fx-btn fx-btn--secondary text-xs border-red-300 text-red-700 hover:bg-red-50"
                data-testid={`reject-${job.job_id}`}>
                <XCircle size={12} /> {t("adminX.jobs.rejectBtn")}
              </button>
            </>
          )}
          {!flagged && !job.is_deleted && (
            <Link to={`/client/jobs/${job.job_id}`} className="fx-btn fx-btn--secondary text-xs" target="_blank" rel="noopener noreferrer">
              {t("adminX.jobs.viewBtn")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function Pager({ t, page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-6 flex items-center justify-center gap-3" data-testid="pager">
      <button onClick={onPrev} disabled={page === 0}
        className="fx-btn fx-btn--secondary text-xs disabled:opacity-40" data-testid="pager-prev">
        <ChevronLeft size={14} /> {t("adminX.jobs.prev")}
      </button>
      <span className="text-sm text-smoke font-mono">{t("adminX.jobs.page", { n: page + 1, total: totalPages })}</span>
      <button onClick={onNext} disabled={page >= totalPages - 1}
        className="fx-btn fx-btn--secondary text-xs disabled:opacity-40" data-testid="pager-next">
        {t("adminX.jobs.next")} <ChevronRight size={14} />
      </button>
    </div>
  );
}
